import { forwardRef } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { FcComboChart, FcDataConfiguration, FcAbout, FcTreeStructure } from 'react-icons/fc';

import { getIndicatorColorClass } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/colorUtils';

const GENERIC_BUTTONS = {
    DETAILS: {
        label: 'Details',
        icon: FcDataConfiguration,
        className: 'hover:bg-purple-50'
    },
    HISTORY: {
        label: 'History',
        icon: FcComboChart,
        className: 'hover:bg-cyan-50'
    },
    SHOW: {
        label: 'Show',
        icon: FcTreeStructure,
        className: 'hover:bg-blue-50'
    }
};

const Node = forwardRef(function Node(props, ref) {
    return (
        <div ref={ref} className={'w-full ' + (props.fit ? 'h-full' : 'h-auto') + ' flex flex-col items-start rounded-2xl shadow-lg bg-gray-50 border overflow-hidden'}>

            {props.title && <div className='w-full flex justify-between'>

                <div className='flex items-center justify-center gap-10 border-b border-r bg-white rounded-br-xl px-14 py-14 shadow-md'>

                    <div className='flex flex-col gap-0.5'>

                        <div className='flex items-center gap-6'>
                            {props.icon && <props.icon className='text-lg shrink-0 opacity-75' />}
                            <span className='text-base font-bold leading-none whitespace-nowrap'>{props.title}</span>
                        </div>

                    </div>

                </div>

            </div>}

            <div className='w-full flex-1 justify-center flex flex-col gap-8 p-12'>
                {props.loading ? <NodeLoader /> : props.children}
            </div>

        </div>
    );
});

export default Node;

export function NodeLoader(props) {
    return (
        <div className='w-full min-h-74 flex flex-col items-center justify-center gap-8 rounded-xl px-12 py-16'>
            <CircularProgress size={20} thickness={4} />
            <div className='text-10 font-semibold text-gray-400 text-center'>
                {props.label || 'Loading data...'}
            </div>
        </div>
    );
}

export function Button(props) {
    const genericConfig = props.genericType ? GENERIC_BUTTONS[props.genericType] : null;

    const config = {
        label: props.label || genericConfig?.label || '',
        icon: props.icon || genericConfig?.icon || null,
        className: props.className || genericConfig?.className || ''
    };

    const Icon = config.icon;

    return (
        <button className={'flex-1 flex items-center justify-center gap-6 transition rounded-full px-16 py-8 shadow-sm hover:shadow-none bg-gray-200 ' + config.className + (props.disabled ? ' filter grayscale cursor-not-allowed' : '')} onClick={props.onClick} disabled={props.disabled}>
            {Icon && <div className={'text-sm mb-1 ' + (props.disabled ? 'opacity-50' : '')}><Icon /></div>}
            <div className={'text-xs font-medium text-center ' + (props.disabled ? 'opacity-50' : '')}>{config.label}</div>
        </button>
    );
}

export function NodeMessage(props) {
    return (
        <div className='w-full flex gap-6 rounded-xl border bg-white px-12 py-10 text-11 font-semibold text-gray-700 shadow-inner'>
            <FcAbout className='text-14' />
            <span>{props.value}</span>
        </div>
    );
}

export function NodeValue(props) {
    const isListItem = props.isListItem === true;

    return (
        <div className={'w-full flex items-center justify-between gap-12 rounded-xl ' + (isListItem ? 'px-4 py-2' : 'border bg-white pl-12 pr-6 py-6 shadow-inner')}>
            <div className={'' + (isListItem ? 'text-10' : 'text-11 font-semibold')}>{props.label}</div>
            {props.value && <NodeChip label={props.value} severity={props.severity} small={isListItem} />}
        </div>
    );
}

export function NodeValueList(props) {
    const emptyLabel = props.emptyLabel || 'No data available';

    if (!props.rows?.length) {
        return (
            <div className='w-full flex flex-col gap-2 shadow-inner p-4 bg-white rounded-xl border text-11'>
                <div className='px-8 py-1 rounded-lg bg-gray-200 opacity-50'>
                    {emptyLabel}
                </div>
            </div>
        );
    }

    return (
        <div className='w-full flex flex-col gap-2 shadow-inner p-4 bg-white rounded-xl border text-11'>
            {props.rows.map((row, i) => (
                <div key={row.label || i} className='border-b last:border-0 py-2'>
                    <NodeValue label={row.label} value={row.value} severity={row.severity} isListItem />
                </div>
            ))}
        </div>
    );
}

export function NodeChip(props) {
    let color = 'gray';

    if (props.severity === 'success') color = 'green';
    if (props.severity === 'error') color = 'red';
    if (props.severity === 'warning') color = 'yellow';

    return (
        <div className={'rounded-lg font-semibold text-10 ' + (props.small ? 'px-7 py-2.5' : 'px-8 py-5') + ' ' + getIndicatorColorClass(color)}>
            {props.label}
        </div>
    );
}